import { useCandidateAuthStore } from "./candidateAuthStore";

/** Délai après lequel on considère qu'un `beforeunload` a été annulé par le
    candidat. Les timers ne tournent pas pendant que le dialogue modal du
    navigateur est affiché : ce délai part donc du clic sur « Rester ». */
const DELAI_ANNULATION_MS = 2000;

/**
 * Hook personnalisé pour gérer la déconnexion des applications candidates.
 *
 * Recharge la page courante ; le store d'authentification n'est vidé qu'au
 * moment où le navigateur décharge réellement la page. La modale de login
 * réapparaît alors automatiquement car le token sera vide.
 *
 * @example
 * ```tsx
 * const { logout } = useCandidateLogout();
 *
 * <Navbar onLogout={logout} />
 * ```
 */
export function useCandidateLogout() {
  const clear = useCandidateAuthStore((state) => state.clear);

  const logout = () => {
    // Le store est vidé sur `pagehide`, pas tout de suite : un template peut
    // installer un garde `beforeunload` (ex. réordonnancement PAMI non
    // sauvegardé) et le candidat peut choisir de rester sur la page. Vider
    // avant laisserait alors l'app montée avec un token vide, et toutes les
    // sauvegardes suivantes échoueraient en silence.
    const viderStore = () => clear();
    window.addEventListener("pagehide", viderStore, { once: true });

    // Départ annulé : on retire le garde, sinon il se déclencherait à la
    // prochaine fermeture d'onglet et déconnecterait le candidat à son insu.
    const nettoyage = setTimeout(() => {
      window.removeEventListener("pagehide", viderStore);
    }, DELAI_ANNULATION_MS);
    window.addEventListener(
      "pagehide",
      () => clearTimeout(nettoyage),
      { once: true },
    );

    // Retirer ?token= de l'URL avant de recharger. AuthGuard/ParcoursGuard
    // lisent ce param EN PRIORITÉ sur le store : sans ce nettoyage, le reload
    // re-authentifie aussitôt et la déconnexion n'a aucun effet visible.
    // Le param n'est ajouté qu'en dev (voir parcours-view.tsx), donc ce cas ne
    // se produit qu'en local — mais le nettoyage est sans effet ailleurs.
    const url = new URL(window.location.href);
    if (url.searchParams.has("token")) {
      url.searchParams.delete("token");
      // replace() plutôt que assign() : pas d'entrée d'historique, donc le
      // bouton Retour ne ramène pas sur l'URL contenant le token.
      window.location.replace(url.toString());
      return;
    }

    window.location.reload();
  };

  return { logout };
}
